<template>
  <div class="card shadow-lg mb-5">
    <div class="card-body">
      <!-- Vertical tabs -->
      <div class="row">
        <div v-if="!create" class="col-3 tabs">
          <ul class="nav nav-tabs flex-column pointer" role="tablist">
            <li class="nav-item">
              <a href="" data-bs-target="#general" data-bs-toggle="tab" role="tab"
                class="nav-link text-decoration-none active">
                {{ $t('settings.general') }}
              </a>
            </li>
            <li class="nav-item">
              <a href="" data-bs-target="#access" data-bs-toggle="tab" role="tab"
                class="nav-link text-decoration-none">
                Access
              </a>
            </li>
            <li class="nav-item">
              <a href="" data-bs-target="#messages" data-bs-toggle="tab" role="tab"
                class="nav-link text-decoration-none">
                Personalize
              </a>
            </li>
            <li class="nav-item">
              <a href="" data-bs-target="#features" data-bs-toggle="tab" role="tab"
                class="nav-link text-decoration-none">
                Features
              </a>
            </li>
            <li class="nav-item">
              <a href="" data-bs-target="#media" data-bs-toggle="tab" role="tab"
                class="nav-link text-decoration-none">
                {{ $t('nav.media') }}
              </a>
            </li>
            <li class="nav-item">
              <a href="" data-bs-target="#type" data-bs-toggle="tab" role="tab"
                class="nav-link text-decoration-none">
                {{ $t('type') }}
              </a>
            </li>
            <li class="nav-item">
              <a href="" data-bs-target="#delete" data-bs-toggle="tab" role="tab"
                class="nav-link text-decoration-none text-danger fw-semibold">
                {{ $t('network.deleteNetwork') }}
              </a>
            </li>
          </ul>
        </div>
        <div :class="create ? 'col-12' : 'col-9'">
          <div v-if="!busy" id="myTabContent" class="tab-content">
            <!--General-->
            <div id="general" role="tabpanel" aria-labelledby="general-tab" class="tab-pane fade show active">
              <h4 v-if="!create">{{ $t('settings.general') }}</h4>
              <h5 v-if="!create">Add or update basic information related to your network.</h5>
              <form v-if="authorizedAccess()" @submit.prevent="onSubmit">
                <h5 v-if="create">{{ $t('network.launchDesc') }}</h5>
                <div class="form-group mb-3 mt-1">
                  <label id="input-group-1" for="name">{{ $t('network.name') }}</label><span class="align-top text-danger fw-bold">*</span>
                  <input id="name" v-model="form.name" data-test="name" class="form-control" required placeholder="Enter a name for your network" />
                </div>

                <div v-if="networkMatches.length"><br>
                  <p class="fw-semibold mb-3">We found similar networks! Interested in joining any of these?</p>
                  <div class="bg-secondary pt-1 pb-0 px-4 rounded">
                    <div v-for="network in networkMatches" :key="network">
                      <div class="d-flex mt-3" style="justify-content: space-between; align-items: center; font-size: medium;">
                        <span class="me-2">{{ network.name }}</span>
                        <div class="justify-content-end">
                          <a :href="'/networks/'+network.slug" target="_blank" class="me-1 fw-bold">{{ $t('network.join') }}</a>
                        </div>
                      </div>
                    </div><br>
                  </div>
                </div>

                <div class="form-group mb-3">
                  <label id="input-group-2" for="short_name">{{ $t('network.networkForm.shortName') }}</label><span class="align-top text-danger fw-bold">*</span>
                  <input id="short_name" v-model="form.short_name" data-test="short_name" class="form-control" required placeholder="Enter a short name for your network" />
                  <p class="alert rounded-1 alert-warning text-black fw-semibold mt-3 py-2 px-3" style="font-size: small;">
                    Network permalinks are derived from the short name assigned to the network.
                    If the short name is already taken, the permalink will adjust automatically (e.g., 'oncology' becomes 'oncology-1').
                  </p>
                </div>

                <div class="form-group mb-3">
                  <label id="input-group-3" for="purpose">{{ $t('network.networkForm.purpose') }}</label><span class="align-top text-danger fw-bold">*</span>
                  <textarea id="purpose" v-model="form.purpose" data-test="purpose" class="form-control" required></textarea>
                  <small class="text-secondary">This is a short description that will be displayed in the landing page and network homepage.</small>
                </div>

                <div v-if="$auth.hasRole('super-admin') && !create" class="form-group mb-3">
                  <label id="input-group-3" for="vanity_link">Vanity Link</label>
                  <v-select
                    v-model="selectedVanityLink"
                    :options="vanity_links"
                    :get-option-label="option => option.slug + ' ('+ option.link +')'"
                    class="mt-2"
                    placeholder="Select a vanity link"
                    @input="option => form.vanity_link_id = option.id"
                  ></v-select>
                  <small class="text-secondary">This is a short link associated with the recurrent meeting of the network.</small>
                </div>

                <div v-if="!create">
                  <div class="form-group my-2 d-grid">
                    <label id="input-group-7">
                      {{ $t('network.networkForm.logo') }} ({{ $t('network.networkForm.supportedImgFormats') }})
                    </label>
                    <a class="btn btn-sm btn-light m-1 p-1 px-2 rounded-3 py-5 fs-5" @click="toggleProfileImageModal">
                      <i class="fa fa-upload me-1"></i>
                      <span v-if="form.logo_image">{{ $t('replace-network-logo') }}</span><br/>
                      <img v-if="form.logo_url" :src=form.logo_url alt="" height="50" width="50">
                      <span v-if="!form.logo_image">{{ $t('NetworkForm.upload-network-logo') }}</span>
                    </a>
                    <my-upload
                      v-if="show_profile_upload_modal"
                      v-model="show_profile_upload_modal"
                      field="img"
                      img-format="jpg"
                      :width="300"
                      :height="300"
                      :langType="'en'"
                      @crop-success="uploadProfileImage"
                    ></my-upload>
                  </div>
                  <div v-if="form.logo_image" class="form-group" style="margin-bottom: 2rem;">
                    <div>
                      <span class="text-danger me-2 pointer fw-semibold float-end" @click="deleteImage('logo_image', form.logo_image)">
                        {{ $t('NetworkForm.delete-uploaded-file') }}
                      </span>
                    </div>
                  </div>

                  <div class="form-group mb-2 d-grid">
                    <label id="input-group-7">
                      {{ $t('network.networkForm.cover') }} ({{ $t('network.networkForm.supportedImgFormats') }})
                    </label>
                    <a class="btn btn-sm btn-light m-1 p-1 px-2 rounded-3 py-5 fs-5" @click="toggleCoverImageModal">
                      <i class="fa fa-upload me-1"></i>
                      <span v-if="form.cover_image">{{ $t('replace-network-cover') }}</span><br/>
                      <img v-if="form.cover_url" :src=form.cover_url alt="" height="50" width="75">
                      <span v-if="!form.cover_image">{{ $t('NetworkForm.upload-network-cover') }}</span>
                    </a>
                    <my-upload
                      v-if="show_cover_upload_modal"
                      v-model="show_cover_upload_modal"
                      field="img"
                      img-format="jpg"
                      :width="820"
                      :height="312"
                      :langType="'en'"
                      @crop-success="uploadCoverImage"
                    ></my-upload>
                  </div>
                  <div v-if="form.cover_image" class="form-group mb-5">
                    <div>
                      <span class="text-primary ms-2 pointer fw-semibold float-start" @click="!form.is_cover_visible">
                        <span v-if="!form.is_cover_visible">Display cover</span>
                        <span v-else>Hide cover</span>
                      </span>
                      <span class="text-danger me-2 pointer fw-semibold float-end" @click="deleteImage('cover_image', form.cover_image)">
                        {{ $t('NetworkForm.delete-uploaded-file') }}
                      </span>
                    </div>
                  </div>

                  <div class="form-group mb-3">
                    <label id="input-group-6" label-for="input-6">{{ $t('special-highlight') }}</label>
                    <Editor
                      id="input-6"
                      v-model="form.about"
                      :api-key="tinyMceApiKey"
                      :init="{
                        height: 300,
                        menubar: false,
                        images_upload_handler: uploadImageTinyMceBlob,
                        automatic_uploads: true,
                        file_picker_types: 'image',
                        browser_spellcheck: true,
                        paste_as_text: true,
                        relative_urls : false,
                        remove_script_host : false,
                        document_base_url : home,
                        plugins: [
                          'advlist autolink lists link image charmap print preview anchor',
                          'searchreplace visualblocks code fullscreen',
                          'insertdatetime media table paste code help wordcount',
                        ],
                        toolbar:
                          'undo redo | formatselect | bold italic backcolor | \
                        alignleft aligncenter alignright alignjustify | \
                        bullist numlist outdent indent | removeformat | help | link image ',
                        image_advtab: true,
                        image_class_list: [
                          { title: 'None', value: '' },
                          { title: 'Rounded', value: 'rounded' },
                          { title: 'Rounded (circle)', value: 'rounded-circle' },
                          { title: 'Margin Right', value: 'mr-5' },
                          { title: 'Picture', value: 'img-fluid img-thumbnail' },
                          { title: 'Fluid', value: 'img-fluid' },
                        ],
                      }"
                    />
                  </div>

                  <div v-if="busy" class="text-center">
                    <lottie-player
                      autoplay
                      loop
	                    speed="2.5"
                      src="/img/animations/custom_loader.json"
                      style="height: 130px;"
                      >
                    </lottie-player>
                    <p id="cancel-label">{{ $t('network.networkForm.loading') }}</p>
                  </div>
                </div>

                <button v-if="create" id="save" data-test="save" type="submit" class="btn btn-primary mt-1 me-1" :disabled="busy" :label="this.$t('network.createNetwork')">
                  {{ $t('network.createNetwork') }}
                </button>

              </form>
            </div>
            <!--Access-->
            <div id="access" role="tabpanel" aria-labelledby="access-tab" class="tab-pane fade">
              <h4>Access</h4>
              <h5>Select or update network access for users.</h5>
              <div class="form-group mb-1">
                <div class="row mt-2">
                  <div class="form-check mb-2">
                    <input
                      id="private_network"
                      v-model="form.private"
                      name="network_access"
                      data-test="private_network"
                      type="radio"
                      :value=true
                      class="form-check-input"
                      @change="form.public = false"
                    />
                    <label class="form-check-label">
                      {{ $t('network.networkForm.privateNetwork') }}
                    </label>
                    <div class="form-text text-secondary">
                      Only visible to network members. Can only be join with an invitation.
                    </div>
                  </div>
                  <div class="form-check mb-2">
                    <input
                      id="public_network"
                      v-model="form.public"
                      name="network_access"
                      data-test="public_network"
                      type="radio"
                      :value=true
                      class="form-check-input"
                      @change="form.private = false"
                    />
                    <label class="form-check-label">
                      Public Network
                    </label>
                    <div class="form-text text-secondary">
                      Publicly listed and discoverable on Resonance Networks.
                    </div>
                  </div>
                </div>
              </div>
              <div v-if="form.public" class="form-group form-check form-switch ms-3">
                <input id="moderation-checkbox" v-model="form.moderation" data-test="moderation-checkbox" type="checkbox" class="form-check-input" switch
                style="font-size: 11px;" />
                <label class="form-check-label" style="vertical-align: super;">Needs member moderation</label>
                <div class="form-text text-secondary mt-0">
                  Allows you to review basic details of the users and approve or decline their membership request.
                  <br/>
                  You'll also need to define a moderator who can oversee the moderation process.
                </div>
              </div>
            </div>
            <!--Messages-->
            <div id="messages" role="tabpanel" aria-labelledby="messages-tab" class="tab-pane fade">
              <h4>Personalize</h4>
              <h5 v-if="form.moderation">
                You can customize the announcements sent to users when they join the network or if their membership request is declined.
                If no custom announcement is set, the default will be used.
              </h5>
              <h5 v-else>
                You can customize the announcement sent to users when they join the network.
                If no custom announcement is set, the default will be used.
              </h5>
              <form class="personalize-custom" style="text-decoration: none !important;" @submit.prevent="updateMembershipMessage">
                <div v-if="form.moderation" class="row pt-2">
                  <div class="col-3">
                    <span role="button" :class="selectedMessageType === 'welcome' ? 'text-primary under_lined' : ''" class="tab"
                    @click="selectedMessageType = 'welcome', showCurrentMessageMembershipPreview()">
                      Welcome message
                    </span>
                  </div>
                  <div class="col-4">
                    <span role="button" :class="selectedMessageType === 'rejection' ? 'text-primary under_lined' : ''" class="tab ms-3"
                    @click="selectedMessageType = 'rejection', showCurrentMessageMembershipPreview()">
                      Rejection message
                    </span>
                  </div>
                  <hr style="opacity: 0.5; height: 3px;">
                </div>

                <!-- Welcome Message Section -->
                <div v-if="selectedMessageType === 'welcome' || !form.moderation" :key="'welcome-message'" class="message-content">
                  <div class="form pb-3">
                    <div class="col-8" style="display: flex;">
                      <label class="form-check-label fs-7 col-4 pe-2">
                        <input
                          id="welcome_message_enabled"
                          v-model="form.welcome_message_enabled"
                          :value="false"
                          data-test="welcome_message_enabled"
                          type="radio"
                          name="welcome_message_option"
                          class="form-check-input check_input"
                        />
                        <span class="check_span">&nbsp;Use default</span>
                      </label>
                      <label class="form-check-label fs-7 col-4 pe-2">
                        <input
                          id="custom_welcome_message_edit"
                          v-model="form.welcome_message_enabled"
                          :value="true"
                          data-test="custom_welcome_message_edit"
                          type="radio"
                          name="welcome_message_option"
                          class="form-check-input check_input"
                        />
                        <span class="check_span">&nbsp;Use custom</span>
                      </label>
                    </div>
                  </div>

                  <!-- Default Welcome Message -->
                  <div v-if="form.welcome_message_enabled === false">
                    <p v-html="current_message_membership_preview_html"></p>
                  </div>

                  <!-- Custom Welcome Message -->
                  <div v-if="form.welcome_message_enabled === true">
                    <div class="col-md-12 ps-0 mb-1">
                      <div class="form-group py-2">
                        <label class="fw-bold">Subject</label>
                        <input id="welcome_message_subject" v-model="form.welcome_message_subject" data-test="welcome_message_subject" type="text" class="custom-msg-input form-control" />
                        <small>This is the announcement subject.</small>
                      </div>
                    </div>
                    <label class="fw-bold">{{ $t('network.member.message') }}</label>
                    <Editor
                      v-model="form.welcome_message_body"
                      :api-key="tinyMceApiKey"
                      :init="tinyMceInitOptions"
                    />
                    <div class="d-flex justify-content-between">
                      <span id="custom_message_membership_preview" data-test="custom_message_membership_preview" class="text-primary fw-bold pointer"
                      @click="custom_message_membership_preview = !custom_message_membership_preview, showCustomMessageMembershipPreview()">
                        See preview
                      </span>
                    </div>
                    <button id="save" data-test="save" type="submit" class="btn btn-primary mt-3 me-1" :disabled="busy">
                      {{ $t('network.user.save') }}
                    </button>
                  </div>
                </div>

                <!-- Rejection Message Section -->
                <div v-if="selectedMessageType === 'rejection'" :key="'rejection-message'" class="message-content">
                  <div class="form pb-3">
                    <div class="col-8" style="display: flex;">
                      <label class="form-check-label fs-7 col-4">
                        <input
                          id="rejection_message_enabled"
                          v-model="form.rejection_message_enabled"
                          :value="false"
                          data-test="rejection_message_enabled"
                          type="radio"
                          name="rejection_message_option"
                          class="form-check-input check_input"
                        />
                        <span class="check_span">&nbsp;Use default</span>
                      </label>
                      <label class="form-check-label fs-7 col-4">
                        <input
                          id="custom_rejection_message_edit"
                          v-model="form.rejection_message_enabled"
                          :value="true"
                          data-test="custom_rejection_message_edit"
                          type="radio"
                          name="rejection_message_option"
                          class="form-check-input check_input"
                        />
                        <span class="check_span">&nbsp;Use custom</span>
                      </label>
                    </div>
                  </div>

                  <!-- Default Rejection Message -->
                  <div v-if="form.rejection_message_enabled === false">
                    <p v-html="current_message_membership_preview_html"></p>
                  </div>

                  <!-- Custom Rejection Message -->
                  <div v-if="form.rejection_message_enabled === true">
                    <div class="col-md-12 ps-0">
                      <div class="form-group py-2">
                        <label class="fw-bold">Subject</label>
                        <input id="rejection_message_subject" v-model="form.rejection_message_subject" data-test="rejection_message_subject" type="text" class="custom-msg-input form-control" />
                        <small>This is the announcement subject.</small>
                      </div>
                    </div>
                    <label class="fw-bold">{{ $t('network.member.message') }}</label>
                    <Editor
                      v-model="form.rejection_message_body"
                      :api-key="tinyMceApiKey"
                      :init="tinyMceInitOptions"
                    />
                    <div class="d-flex justify-content-between">
                      <span id="custom_message_membership_preview" data-test="custom_message_membership_preview" class="text-primary fw-bold pointer"
                      @click="custom_message_membership_preview = !custom_message_membership_preview, showCustomMessageMembershipPreview()">
                        See preview
                      </span>
                    </div>
                    <button id="save" data-test="save" type="submit" class="btn btn-primary mt-3 me-1" :disabled="busy">
                      {{ $t('network.user.save') }}
                    </button>
                  </div>
                </div>
              </form>
            </div>
            <!--Features-->
            <div id="features" role="tabpanel" aria-labelledby="features-tab" class="tab-pane fade">
              <h4>Features</h4>
              <h5>Choose or update from the wide range of features available for your network.</h5>
                <div class="form-group mb-3">
                  <div class="row mt-3">
                    <div class="form-check mb-2">
                      <input
                        id="network_members"
                        v-model="form.is_members_tab_visible"
                        data-test="network_members"
                        type="checkbox"
                        class="form-check-input"
                      />
                      <label class="form-check-label">
                        {{ $t('network.tabMembers') }}
                      </label>
                      <div class="form-text text-secondary">
                        Display all network members in list view with search and sort abilities, along with other role based features.
                      </div>
                    </div>
                    <div class="form-check mb-2">
                      <input
                        id="network_announcements"
                        v-model="form.is_messages_tab_visible"
                        data-test="network_announcements"
                        type="checkbox"
                        class="form-check-input"
                      />
                      <label class="form-check-label">
                        {{ $t('announcements') }}
                      </label>
                      <div class="form-text text-secondary">
                        Send messages to all network members.
                      </div>
                    </div>
                    <div class="form-check mb-2">
                      <input
                        id="network_meetings"
                        v-model="form.is_meetings_tab_visible"
                        data-test="network_meetings"
                        type="checkbox"
                        class="form-check-input"
                      />
                      <label class="form-check-label">
                        {{ $t('network.tabMeetings') }}
                      </label>
                      <div class="form-text text-secondary">
                        Schedule zoom meetings directly, allow participants to view and join upcoming meetings, and more.
                      </div>
                    </div>
                    <div class="form-check mb-2">
                      <input
                        id="network_metrics"
                        v-model="form.is_metrics_tab_visible"
                        data-test="network_metrics"
                        type="checkbox"
                        class="form-check-input"
                      />
                      <label class="form-check-label">
                        {{ $t('network.usageMetrics') }}
                      </label>
                      <div class="form-text text-secondary">
                        Track engagement from users on media items and other metrics.
                      </div>
                    </div>
                    <div v-if="form.slug == 'wb'" class="form-check mb-2">
                      <input
                        id="network_blog"
                        v-model="form.is_blog_tab_visible"
                        data-test="network_blog"
                        type="checkbox"
                        class="form-check-input"
                      />
                      <label class="form-check-label">
                        {{ $t('blog') }}
                      </label>
                      <div class="form-text text-secondary">
                        Display published posts in the Resonance website.
                      </div>
                    </div>
                    <div class="form-check mb-2">
                      <input
                        id="network_blog"
                        v-model="form.is_cover_visible"
                        data-test="network_cover"
                        type="checkbox"
                        class="form-check-input"
                      />
                      <label class="form-check-label">
                        {{ $t('network.networkForm.cover') }}
                      </label>
                      <div class="form-text text-secondary">
                        Display a cover image on the network homepage.
                      </div>
                    </div>
                  </div>
                </div>
            </div>
            <!--Media-->
            <div id="media" role="tabpanel" aria-labelledby="media-tab" class="tab-pane fade">
              <h4>Media</h4>
              <h5>Choose the media permissions for this network.</h5>
              <div class="form-group mb-3">
                <PermissionList v-model="form.default_media_permission" :value="form.default_media_permission" />
              </div>
            </div>
            <!--Type-->
            <div id="type" role="tabpanel" aria-labelledby="type-tab" class="tab-pane fade">
              <h4>{{ $t('type') }}</h4>
              <h5>Select or update categories for your network.</h5>
              <div class="form-group mb-3">
                <div class="row mt-2">
                  <div>
                    <div class="form-check mb-2">
                      <input
                        id="network_is_global"
                        v-model="form.is_global"
                        name="network_type"
                        data-test="network_is_global"
                        type="radio"
                        :value=true
                        class="form-check-input"
                        @change="form.is_regional = false"
                      />
                      <label class="form-check-label">
                        {{ $t('global') }}
                      </label>
                      <div class="form-text text-secondary">
                        A network that caters to global audience.
                      </div>
                    </div>
                    <div class="form-check mb-2">
                      <input
                        id="network_is_regional"
                        v-model="form.is_regional"
                        name="network_type"
                        data-test="network_is_regional"
                        type="radio"
                        :value=true
                        class="form-check-input"
                        @change="form.is_global = false"
                      />
                      <label class="form-check-label">
                        {{ $t('regional') }}
                      </label>
                      <div class="form-text text-secondary">
                        A network that caters to specific regional audience, for e.g. Latin America.
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="form-group mb-3">
                <span>Choose the language most prevalent across your network.</span>
                <v-select
                  v-model="form.language"
                  :options="languages"
                  label="language"
                  class="mt-2"
                  placeholder="Select one or more languages"
                  multiple
                ></v-select>
              </div>

              <div class="form-group mb-3">
                <span>Add tags that help explain what's the subject expertise of your network.</span>
                <vue-tags-input
                  v-model="tag"
                  placeholder=""
                  :add-from-paste="false"
                  :autocomplete-items="allTags"
                  :separators="[',', ';']"
                  :tags="tags"
                  class="border mt-2"
                  style="max-width: unset;"
                  @tags-changed="tagUpdateEvent">
                </vue-tags-input>
              </div>
              <div v-if="form.tags" class="form-group">
                <div v-for="(tag,index) in sortedArray(form.tags)" :key="index" role="group" aria-label="" class="btn-group btn-sm ps-1 m-1">
                  <button type="button" class="btn btn-light bg-white rounded-pill position-relative"
                  style="padding: 0.2rem 0.8rem !important; box-shadow: 0px 10px 30px 0px rgba(0, 0, 0, 0.15);">
                    <span>#{{ tag.name }}</span>
                    <span id="remove_tag" data-test="remove_tag" class="position-absolute start-100 translate-middle badge rounded bg-danger" @click="removeTag(tag)">
                    <i class="fa-solid fa-minus fa-xs"></i>
                    </span>
                  </button>
                </div>
              </div>
            </div>
            <div id="delete" role="tabpanel" aria-labelledby="delete-tab" class="tab-pane fade">
              <h4>{{ $t('network.deleteNetwork') }}</h4>
              <h5 v-if="$auth.can('delete', form.can)">
                Deleting a network is a permanent action that cannot be undone. If you wish to restore it afterwards you'll need to contact support.
              </h5>
              <h5 v-else>
                {{ $t('network.contactForDelete') }}
              </h5>
              <button v-if="$auth.can('delete', form.can)" id="delete_network" data-test="delete_network" class="btn btn-danger mt-2" @click.prevent="showDeleteModal = true">
                {{ $t('network.deleteNetwork') }}
              </button>
              <button v-else id="contact_support" data-test="contact_support" class="btn btn-primary mt-2" @click="contactNow">
                {{ $t('contact-support') }}
              </button>
            </div>
          </div>
          <div v-else class="text-center m-6">
            <lottie-player
              autoplay
              loop
	            speed="2.5"
              src="/img/animations/custom_loader.json"
              style="height: 130px;"
              >
            </lottie-player>
          </div>
        </div>
      </div>
      <ConfirmModalWithInputAndCheckbox
        :placeholder="this.$t('Showtype-delete-to-proceed')"
        confirmationText="DELETE"
        okTitle="Proceed"
        :checkboxMessage="$t('network.delete.permanentCheckbox')"
        :showModal="showDeleteModal"
        @input-modal-confirmed="deleteNetwork"
        @input-modal-cancel="showDeleteModal = false"
      >
        {{ $t('NetworkForm.are-you-sure-you-want-to-delete-this-network') }}
      </ConfirmModalWithInputAndCheckbox>
      <b-modal
        id="customMessageMembershipPreviewModal"
        size="lg"
        hide-footer
        :title="(!form.moderation || this.selectedMessageType === 'welcome') ? form.welcome_message_subject : form.rejection_message_subject"
        body-class="p-0"
        @hide="custom_message_membership_preview = false"
      >
        <p v-html="custom_message_membership_preview_html"></p>
      </b-modal>
    </div>
  </div>
</template>
<script>
import ConfirmModalWithInputAndCheckbox from '@/components/commons/Modals/ConfirmModalWithInputAndCheckbox.vue';
import NetworkService from '@/services/NetworkService.js';
import PermissionList from '../commons/Networks/PermissionList.vue';
import Editor from '@tinymce/tinymce-vue';
import myUpload from 'vue-image-crop-upload/upload-2.vue';
import { loadProgressBar } from 'axios-progress-bar';
import axios from 'axios';
import { sortBy, debounce } from 'lodash';
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';
import VueTagsInput from '@johmun/vue-tags-input';
import { mapState } from 'vuex';

export default {
  components: {
    ConfirmModalWithInputAndCheckbox,
    Editor,
    'my-upload': myUpload,
    PermissionList,
    vSelect,
    VueTagsInput,
   },
  props: {
    form: {
      type: Object,
      default: null,
    },
    busy: {
      type: Boolean,
      default: false,
    },
    pendingInvites: {
      type: Array,
      default: null,
    },
    create: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showDeleteModal: false,
      defaultMediaPermissions: ['public', 'authorized', 'private', 'unlisted'],
      show_profile_upload_modal: false,
      show_cover_upload_modal: false,
      tinyMceApiKey: process.env.VUE_APP_TINYMCE_API_KEY,
      protectedWords: ['messages', 'media', 'all', 'members', 'meetings', 'dashboard', 'templates', 'create', 'edit', 'delete', 'update'],
      home: window.location.origin,
      files: [],
      s3Params: {},
      options: {}, // uploader options
      uploadFinished: false,
      languages: [],
      tag: '',
      tags: [],
      allTags: [],
      existingMediaPermissions: [],
      showConfirmation: true,
      vanity_links: [],
      selectedVanityLink: '',
      networkMatches: [],
      selectedMessageType: 'welcome',
      oldFormCopy: {},
      current_message_membership_preview: false,
      current_message_membership_preview_html: '',
      custom_message_membership_preview: false,
      custom_message_membership_preview_html: '',
      debouncedCheckShortName: debounce(this.checkShortName, 3000), // 3 seconds
    };
  },
  computed: {
    ...mapState('network', ['discoverNetworks']),
    formVisibilityInstructions() {
      return this.$t('network.networkForm.formVisibilityInstructions');
    },
  },
  watch: {
    'form.name': function (newV) {
      if (newV.length >= 2) {
        this.checkSimilarNetworks(newV);
      }
      if (newV.length == 0) {
        this.networkMatches = [];
      }
    },
    'form.public': function (newV) {
      if (newV) {
        // console.log('newV', newV);
      } else {
        this.form.moderation = false;
      }
    },
    'form.private': function (newV) {
      if (newV) {
        this.form.public = !newV;
      }
    },
    'form.moderation': function (newV, oldV) {
      if (newV) {
        // console.log('newV', newV);
      } else {
        // if we're turning moderation off we should check first if there are pending requests
        if (this.pendingInvites.length == 0) {
          // no pending invites
        } else {
          this.$swal.fire({
            icon: 'info',
            title: 'Turning off moderation will automatically approve all pending requests to join this network. Do you want to proceed?',
            showCancelButton: true,
            confirmButtonText : this.$t('ResearchCreds.yes'),
            customClass: {
              confirmButton: 'btn btn-success mx-1',
              cancelButton: 'btn btn-primary mx-1',
            },
            buttonsStyling: false,
          }).then( (result) => {
            if(result.isConfirmed){
              // approving all pending requests
              this.busy = true;
              this.$http.post('networks/' + this.form.slug + '/approve-moderation?query=all')
              .then(() => {
                this.pendingInvites = [];
                this.busy = false;
                this.$swal.fire({
                  icon: 'success',
                  title: 'All pending requests have been approved!',
                });
              });
            } else {
              this.form.moderation = oldV;
            }
          });
        }
      }
    },
    'form.default_media_permission': function(newPermission) {
      this.showConfirmation = false;
      const existingPermissions = this.existingMediaPermissions;

      // calculate the sum of media items with different permission levels
      let sumOfOtherPermissions = 0;
      Object.keys(existingPermissions).forEach(permission => {
        if (permission !== newPermission) {
          sumOfOtherPermissions += existingPermissions[permission];
        }
      });

      const confirmationMessage = `There ${sumOfOtherPermissions === 1 ? 'is' : 'are'} ${sumOfOtherPermissions} media ${sumOfOtherPermissions === 1 ? 'item' : 'items'}
                                  with different permission levels. Do you want to overwrite ${sumOfOtherPermissions === 1 ? 'it' : 'them'}?`;

      if (sumOfOtherPermissions > 0) {
        this.$swal.fire({
          icon: 'warning',
          title: confirmationMessage,
          showCancelButton: true,
          confirmButtonText: 'Yes, overwrite all media',
          cancelButtonText: 'Apply only to future media',
          allowOutsideClick: false,
          allowEscapeKey: false,
          customClass: {
            confirmButton: 'btn btn-success mx-1',
            cancelButton: 'btn btn-primary mx-1',
          },
          buttonsStyling: false,
        }).then( (result) => {
          if(result.isConfirmed){
            this.busy = true;
            this.$http.post('networks/' + this.form.slug + '/update-content-permission', { permission: newPermission })
            .then(() => {
              this.busy = false;
              this.$swal.fire({
                icon: 'success',
                title: 'Media permissions updated!',
              }).then(() => {
                this.showConfirmation = true;
              });
            });
          } else {
            this.showConfirmation = true;
          }
        });
      } else {
        this.showConfirmation = true;
      }
    },
    tag: debounce(function (newTag) {
      this.searchTags(newTag);
    }, 600),
    'form.short_name': function (newShortName) {
      if (!this.create && this.showConfirmation) {
        this.debouncedCheckShortName(newShortName);
      }
    },
    'form': {
      deep: true,
      handler(newForm) {
        if (!this.create && this.showConfirmation) {
          if ((newForm.short_name === this.oldFormCopy.short_name)) {
            this.$bus.emit('network-submit', newForm);
          }
        }
      }
    },
  },
  mounted() {
    this.init();
    this.oldFormCopy = JSON.parse(JSON.stringify(this.form));
    this.form.private = !this.form.public;
    this.$store.dispatch('content_language/loadLanguages').then(languages => {
      this.languages = sortBy(languages, 'language');
    });
    if (!this.create) {
      this.$http.get('networks/' + this.form.slug + '/get-content-permission').then(resp => {
        this.existingMediaPermissions = resp.data;
      });
      this.$http.get('networks/' + this.form.slug + '/get_available_vanity_links').then(resp => {
        this.vanity_links = resp.data;
        this.selectedVanityLink = this.vanity_links.find(option => option.id === this.form.vanity_link_id);
      });
    }
    this.showCurrentMessageMembershipPreview();
  },
  methods: {
    sortedArray(array) {
      return sortBy(array, item => item.name.toLowerCase());
    },
    authorizedAccess() {
      if (this.$auth.auth){
        if (this.create) {
          if (!this.$auth.can('network.create')) {
            this.$swal.fire({
              icon: 'error',
              title: this.$t('unauthorized-access'),
            }).then(() => {
              this.$router.push('/');
            });
          } else {
            return true;
          }
        } else {
          if (!this.form.can.update) {
            this.$swal.fire({
              icon: 'error',
              title: this.$t('unauthorized-access'),
            }).then(() => {
              this.$router.push('/networks/' + this.form.slug);
            });
          } else {
            return true;
          }
        }
      } else {
        this.$bus.emit('show-sign-in');
      }
    },
    /**
     * @define Form events
     */
    init() {
      this.busy = true;
      this.$http
        .post('s3/upload-params')
        .then(resp => {
          this.setupUploadForm(resp.data);
          // upload setup is done!
          this.busy = false;
        })
        .catch(err => {
          this.$bvModal.msgBoxOk(
            this.$t('CreateMediaWithoutCollection.s3-upload-initialization-failed-please-check-console-for-details-or-refresh-the-page-to-try-again')
          );
          console.log('get s3 upload params failed', err);
          this.busy = false;
        });
    },
    setupUploadForm(s3) {
      // see option documentation here: https://github.com/thetutlage/vue-clip
      this.options = {
        url: s3.formAttributes.action,
        maxFilesize: this.maxFilesize || 500, // 500 MB
        autoProcessQueue: true,
        maxFiles: this.maxFiles || 10,
      };
      if (this.acceptedFiles) this.options.acceptedFiles = this.acceptedFiles;

      this.s3Params = s3;
    },
    getFileNameFromUrl(url) {
      return url.substring(url.lastIndexOf('/') + 1);
    },
    checkShortName(newShortName) {
      if (newShortName !== this.oldFormCopy.short_name) {
        this.$swal.fire({
          icon: 'info',
          title: 'Are you sure you want to change the network short name?',
          text: 'This will update the associated link, potentially causing broken links for prior shares.',
          showCancelButton: true,
          confirmButtonText: this.$t('ResearchCreds.yes'),
          customClass: {
            confirmButton: 'btn btn-success mx-1',
            cancelButton: 'btn btn-primary mx-1',
          },
          buttonsStyling: false,
        }).then((result) => {
          if (result.isConfirmed) {
            this.$bus.emit('network-submit', this.form);
          }
        });
      }
    },
    onSubmit() {
      const isRestrictedWord = (word) => {
        return this.protectedWords.includes(word.toLowerCase().trim());
      };

      // check if the entire name matches any protected word
      if (isRestrictedWord(this.form.name)) {
        const restrictedWord = this.protectedWords.find(word => word.toLowerCase() === this.form.name.toLowerCase().trim());
        this.$swal.fire({
          icon: 'error',
          title: `The network name includes the protected word "${restrictedWord}". Please choose a different name.`,
        });
      } else if (isRestrictedWord(this.form.short_name)) {
        const restrictedWord = this.protectedWords.find(word => word.toLowerCase() === this.form.short_name.toLowerCase().trim());
        this.$swal.fire({
          icon: 'error',
          title: `The network name includes the protected word "${restrictedWord}". Please choose a different name.`,
        });
      } else {
        this.$bus.emit('network-submit', this.form);
      }
    },
    updateMembershipMessage () {
      let type = !this.form.moderation ? 'welcome' : this.selectedMessageType;
      if ((type === 'welcome' && (!this.form.welcome_message_subject || !this.form.welcome_message_body))
        || (type === 'rejection' && (!this.form.rejection_message_subject || !this.form.rejection_message_body))) {
        this.$swal.fire({
          icon: 'error',
          title: 'Please fill in the subject and content fields',
        });
      } else {
        this.$swal.fire({
          icon: 'info',
          title: 'Are you sure you want to use this custom message?',
          showCancelButton: true,
          confirmButtonText : this.$t('ResearchCreds.yes'),
          customClass: {
            confirmButton: 'btn btn-success mx-1',
            cancelButton: 'btn btn-primary mx-1',
          },
          buttonsStyling: false,
        }).then( (result) => {
          if(result.isConfirmed) {
            if (type === 'welcome') {
              this.form.welcome_message_enabled = true;
            } else {
              this.form.rejection_message_enabled = true;
            }
            this.$bus.emit('network-submit', this.form);
            this.$swal.fire({
              icon: 'success',
              title: 'Custom message saved!',
            });
          }
        });
      }
    },
    applyDefaultPermission() {
      const isRestrictedWord = (word) => {
        return this.protectedWords.includes(word.toLowerCase().trim());
      };

      // check if the entire name matches any protected word
      if (isRestrictedWord(this.form.name)) {
        const restrictedWord = this.protectedWords.find(word => word.toLowerCase() === this.form.name.toLowerCase().trim());
        this.$swal.fire({
          icon: 'error',
          title: `The network name includes the protected word "${restrictedWord}". Please choose a different name.`,
        });
      } else if (isRestrictedWord(this.form.short_name)) {
        const restrictedWord = this.protectedWords.find(word => word.toLowerCase() === this.form.short_name.toLowerCase().trim());
        this.$swal.fire({
          icon: 'error',
          title: `The network name includes the protected word "${restrictedWord}". Please choose a different name.`,
        });
      } else {
        let action = () => {
          this.$http.post('networks/' + this.form.slug + '/update-content-permission', { permission: this.form.default_media_permission });
          this.onSubmit();
        };
        let msg = this.$t('network.networkForm.updateContentPermissionConfirmation', {
          defaultValue:
            'Are you sure you want to overwrite all media and collections that belong to this network to {{ permission }} permission? (There is no way to undo this.)',
          permission: this.form.default_media_permission,
        });
        this.showConfirmPrompt(action, msg);
      }
    },
    deleteNetwork(deletePermanently) {
      this.$http.post('networks/' + this.form.slug, { permanent: deletePermanently }).then(() => {
        this.$router.push('/networks');
      });
    },
    deleteImage(field, image_path) {
      this.$swal.fire({
        title: this.$t('NetworkForm.are-you-sure-you-want-to-delete-this-image'),
        input: 'text',
        inputLabel: this.$t('Showtype-delete-to-proceed'),
        showCancelButton: true,
        customClass: {
          confirmButton: 'btn btn-danger mx-1',
          cancelButton: 'btn btn-primary mx-1',
        },
        inputValidator: (value) => {
          if (!value) {
            return this.$t('you-need-to-write-something')
          }
          if (value != 'DELETE') {
            return this.$t('wrong-input')
          }
          if (value === 'DELETE') {
            let network_slug = this.form.slug;
            NetworkService.deleteImage(network_slug, field, image_path).then(res => {
              if (res.status == 200) {
                if (res.data.field == 'logo_image') {
                  this.form.logo_image = null;
                  this.form.logo_url = null;
                } else {
                  this.form.cover_image = null;
                  this.form.cover_url = null;
                }
                var field = res.data.field.replaceAll('_', ' ').charAt(0).toUpperCase() + res.data.field.replaceAll('_', ' ').slice(1);
                let message = this.$t('network.edit.media-deleted', {
                  defaultValue: ' {{ field }} deleted successfully',
                  replace: { field },
                });

                this.$swal
                  .fire({
                    icon: 'success',
                    title: this.$t('success'),
                    text: message,
                  });
              }
            });
          }
        }
      })
    },
    /* 
      crop and uploading profile image
    */
    toggleProfileImageModal() {
      this.show_profile_upload_modal = !this.show_profile_upload_modal;
    },
    toggleCoverImageModal() {
      this.show_cover_upload_modal = !this.show_cover_upload_modal;
    },
    uploadProfileImage(imgDataUrl) {
      if (this.form.logo_image) {
        this.$swal.fire({
          icon: 'warning',
          title: this.$t('are-you-sure-you-want-to-replace-the-current-logo'),
          showCancelButton: true,
          confirmButtonText : this.$t('ResearchCreds.yes'),
          customClass: {
            confirmButton: 'btn btn-danger mx-1',
            cancelButton: 'btn btn-primary mx-1',
          },
          buttonsStyling: false
        }).then((result) => {
          if(result.isConfirmed){
            var network_slug = '';
            if (this.form.slug != '') {
              network_slug = this.form.slug;
            }
            this.$http.post('networks/upload-logo/' + network_slug, { network_logo: imgDataUrl, network_slug: network_slug }).then(resp => {
              this.form.logo_image = resp.data.filename;
              this.form.logo_url = resp.data.file_path;
            });
          }
        });
      } else {
        var network_slug = '';
        if (this.form.slug != '') {
          network_slug = this.form.slug;
        }
        this.$http.post('networks/upload-logo/' + network_slug, { network_logo: imgDataUrl, network_slug: network_slug }).then(resp => {
          this.form.logo_image = resp.data.filename;
          this.form.logo_url = resp.data.file_path;
        });
      }
    },
    uploadCoverImage(imgDataUrl) {
      if (this.form.cover_image) {
        this.$swal.fire({
          icon: 'warning',
          title: this.$t('are-you-sure-you-want-to-replace-the-current-cover-image'),
          showCancelButton: true,
          confirmButtonText : this.$t('ResearchCreds.yes'),
          customClass: {
            confirmButton: 'btn btn-danger mx-1',
            cancelButton: 'btn btn-primary mx-1',
          },
          buttonsStyling: false
        }).then((result) => {
          if(result.isConfirmed){
            var network_slug = '';
            if (this.form.slug != '') {
              network_slug = this.form.slug;
            }
            this.$http.post('networks/upload-cover/' + network_slug, { network_cover: imgDataUrl, network_slug: network_slug }).then(resp => {
              this.form.cover_image = resp.data.filename;
              this.form.cover_url = resp.data.file_path;
            });
          }
        });
      } else {
        var network_slug = '';
        if (this.form.slug != '') {
          network_slug = this.form.slug;
        }
        this.$http.post('networks/upload-cover/' + network_slug, { network_cover: imgDataUrl, network_slug: network_slug }).then(resp => {
          this.form.cover_image = resp.data.filename;
          this.form.cover_url = resp.data.file_path;
        });
      }
    },
    toTitleCase(str) {
      return str.toLowerCase().split(' ').map(function (word) {
        return (word.charAt(0).toUpperCase() + word.slice(1));
      }).join(' ');
    },
    contactNow() {
      if (window.umami && typeof window.umami.track === 'function') {
        window.umami.track('open-support-modal');
      }
      this.$bus.emit('show-support-form');
    },
    /**
     * @define Upload To S3 function for TinyMCE and Unlayer
     */
    async uploadImageTinyMceBlob(blobInfo, success) {
      await this.uploadImage(blobInfo.blob(), success, 'tinymce');
    },
    /**
     * @define Upload Image to S3 using Axios
     * @param file
     * @param success
     * @param requestType
     * @returns {Promise<void>}
     */
    async uploadImage(file, success, requestType) {
      let remoteHost = this.s3Params.formAttributes.action;
      var fullPath = file.name;
      var properPath = this.s3Params.uploadPathPrefix + '/' + fullPath;

      var formData = new FormData();
      // update formData before the file is uploaded
      for (let key in this.s3Params.formInputs) {
        var value = this.s3Params.formInputs[key];
        if (key == 'key') value = properPath; // use the proper path calculated (in case its a full folder upload)
        formData.append(key, value);
      }
      this.files.push({
        name: file.name,
        size: file.size,
        fullPath: properPath,
      });
      formData.append('file', file, file.name);
      let axios_instance = axios.create({ headers: { Authorization: '' } });
      loadProgressBar('', axios_instance);
      await axios_instance
        .post(remoteHost, formData)
        .then(() => {
          let attributes = {
            url: remoteHost + '/' + properPath,
            href: remoteHost + '/' + properPath,
          };
          if (requestType === 'tinymce') {
            success(attributes.url);
          } else {
            success({ url: attributes.url });
          }
        })
        .catch(err => {
          console.log('get s3 upload params failed', err);
        });
    },
    searchTags(tagQuery) {
      if (tagQuery.length > 0)
        this.$http.get('tags?limit=20&query=' + tagQuery).then(resp => {
          this.allTags = resp.data.map(a => {
            return { id: a.id, text: a.name };
          });
        });
    },
    tagUpdateEvent(tag) {
      let element = { "name": tag[0].text };
      this.form.tags.push(element);
      this.tags.splice(0);
    },
    removeTag(tag){
      this.form.tags.splice(this.form.tags.indexOf(tag), 1);
    },
    checkSimilarNetworks(name) {
      this.networkMatches = [];

      const matches = this.discoverNetworks.map(network => {
        let coincidenceCount = 0;

        // exact match
        if (network.name.toLowerCase() === name.toLowerCase()) {
          coincidenceCount = 100; // highest priority for exact matches
        }
        // name is a substring of the network name
        else if (network.name.toLowerCase().includes(name.toLowerCase())) {
          coincidenceCount = 75 + (name.length / network.name.length) * 25; // partial matches with higher score for closer matches
        }
        // partial match based on regex pattern
        else if (new RegExp(name.split('').join('.*'), 'i').test(network.name)) {
          coincidenceCount = 50; // lower priority for loose matches
        }

        return { network, coincidenceCount };
      });

      // sort matches by coincidence count (most to least)
      matches.sort((a, b) => b.coincidenceCount - a.coincidenceCount);

      // highlight the top 5 matches
      matches.slice(0, 5).forEach(match => {
        this.networkMatches.push(match.network);
      });
    },
    showCurrentMessageMembershipPreview() {
      let type = !this.form.moderation ? 'welcome' : this.selectedMessageType;
      this.$http.post('networks/' + this.form.slug + '/membership-message/preview', {type: type}).then(resp => {
        this.current_message_membership_preview_html = resp.data;
      });
    },
    showCustomMessageMembershipPreview() {
      let type = !this.form.moderation ? 'welcome' : this.selectedMessageType;
      let content = (!this.form.moderation || this.selectedMessageType === 'welcome') ? this.form.welcome_message_body : this.form.rejection_message_body;
      let subject = (!this.form.moderation || this.selectedMessageType === 'welcome') ? this.form.welcome_message_subject : this.form.rejection_message_subject;
      if (!content || !subject) {
        this.$swal.fire({
          icon: 'error',
          title: 'Subject and content fields are required',
        });
        return;
      }
      this.$http.post('networks/' + this.form.slug + '/membership-message/preview', {type: type, content: content}).then(resp => {
        this.custom_message_membership_preview_html = resp.data;

        if (this.custom_message_membership_preview) {
          this.$bvModal.show('customMessageMembershipPreviewModal');
        } else {
          this.$bvModal.hide('customMessageMembershipPreviewModal');
        }
      });
    },
  },
};
</script>
<style>
.vs__dropdown-toggle {
  border-color: #ccc !important;
}
.vs__selected-options {
  padding: 0px 0px 3px 2px;
}
.tabs .nav .nav-item .active {
  background-color: #f7f7fc !important;
  border:0px !important;
  border-radius:0px;
  cursor: none;
  font-weight: bold;
}
.custom-msg-input {
  background-color: #fff;
  border-color: rgb(195 174 174 / 26%);
  box-shadow: 0 0 0 0 transparent, 0 0.375rem 0.625rem -0.3125rem rgba(0, 95, 154, 0.15);
}
.personalize-custom .nav-pills .nav-item .nav-link {
  border-radius: 10px !important;
  margin-right: 10px;
  border: none;
}
.personalize-custom .nav-pills .nav-item .nav-link.active {
  background: none;
  font-weight: bolder;
  color: #926dde;
}
.under_lined {
  text-decoration-line: underline;
  text-decoration-thickness: 3px;
  text-underline-offset: 9px;
}
.check_span {
  vertical-align: sub !important;
  margin-left: 10px !important;
}
.check_input {
  width: 1.5em !important;
  height: 1.5em !important;
}
</style>